import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  admin,
  type AdminClub,
  type AdminClubDetail as AdminClubDetailT,
  type AdminClubStaff,
  type AdminCreatedStaffInvite,
  type AdminKit,
  type AdminStaffInvite,
  type AdminUserRow,
} from '../../api/admin'
import { ApiError } from '../../api/client'
import { ClubMark } from '../../components/ClubMark'
import { ClubLogoField } from './ClubLogoField'
import { clubAlerts, plural, relativeDay, seatUsage, serviceStatus, tagClass } from './clubOps'
import { ColorField, HEX_RE } from './ColorField'
import { StaffInviteOutcome } from './StaffInviteOutcome'
import { CreateUserModal } from './CreateUserModal'
import { resolveUserTarget, UserPicker } from './UserPicker'
// Un solo rol de club desde el 2026-09-17 (decisión de mg): `manager`. Hasta
// ese día había tres, pero `club_admin` nunca tuvo una acción exclusiva y
// `tecnico` (sólo lectura) no se usaba. Los jugadores no son staff y el
// superadmin es global, no por club.
const INVITE_STATUS_LABEL: Record<AdminStaffInvite['status'], string> = {
  pending: 'pendiente',
  used: 'usada',
  expired: 'vencida',
}

/**
 * /admin/clubs/:clubId — branding edit (name/logo/color), roster (add by
 * email / búsqueda, reusing the idempotent /players endpoint) and staff
 * management (add by email / búsqueda, remove). Un solo rol de club: manager.
 */
export function AdminClubDetail() {
  const { clubId } = useParams<{ clubId: string }>()
  const id = Number(clubId)

  const [club, setClub] = useState<AdminClubDetailT | null>(null)
  const [staff, setStaff] = useState<AdminClubStaff[] | null>(null)
  const [staffInvites, setStaffInvites] = useState<AdminStaffInvite[] | null>(null)
  const [kits, setKits] = useState<AdminKit[] | null>(null)
  const [allClubs, setAllClubs] = useState<AdminClub[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Kits form state.
  const [kitLabel, setKitLabel] = useState('')
  const [kitCode, setKitCode] = useState('')
  // Los kits desactivados quedan en la DB con su historia, pero no en la
  // lista (mg, 2026-10-07): se ven sólo al pedirlo.
  const [showInactiveKits, setShowInactiveKits] = useState(false)
  const activeKits = (kits ?? []).filter((k) => k.active)
  const inactiveKitCount = (kits?.length ?? 0) - activeKits.length
  const visibleKits = showInactiveKits ? (kits ?? []) : activeKits
  const [creatingKit, setCreatingKit] = useState(false)
  const [kitBusyId, setKitBusyId] = useState<number | null>(null)
  const [copiedKitId, setCopiedKitId] = useState<number | null>(null)
  // Reasignación de kit a otro club — target seleccionado por kit.id.
  const [moveTargetByKit, setMoveTargetByKit] = useState<Record<number, string>>({})

  // Branding form state.
  const [name, setName] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [color, setColor] = useState('')
  const [secondaryColor, setSecondaryColor] = useState('')
  const [accentColor, setAccentColor] = useState('')
  const [seatLimit, setSeatLimit] = useState('')
  const [division, setDivision] = useState('')
  const [serviceUntil, setServiceUntil] = useState('')

  // Add-player form state. Mismo criterio que el staff: la persona se
  // identifica por email o eligiéndola de la búsqueda, nunca por un user_id
  // tipeado a mano (mg, 2026-09-17).
  const [playerQuery, setPlayerQuery] = useState('')
  const [playerPick, setPlayerPick] = useState<AdminUserRow | null>(null)
  const [addingPlayer, setAddingPlayer] = useState(false)
  const [playerNotice, setPlayerNotice] = useState<string | null>(null)
  const [createPlayerEmail, setCreatePlayerEmail] = useState<string | null>(null)

  // Add-staff form state. Se identifica por email (o eligiendo de la búsqueda),
  // nunca por user_id tipeado a mano — ver UserPicker.
  const [staffQuery, setStaffQuery] = useState('')
  const [staffPick, setStaffPick] = useState<AdminUserRow | null>(null)
  const [addingStaff, setAddingStaff] = useState(false)
  const [staffNotFound, setStaffNotFound] = useState<string | null>(null)

  // Staff-invite form state.
  const [inviteEmail, setInviteEmail] = useState('')
  const [creatingInvite, setCreatingInvite] = useState(false)
  const [lastInvite, setLastInvite] = useState<AdminCreatedStaffInvite | null>(null)
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false)

  async function reload() {
    setError(null)
    try {
      const [c, s, inv, k, allC] = await Promise.all([
        admin.club(id),
        admin.clubStaff(id),
        admin.staffInvites(id),
        admin.clubKits(id),
        admin.clubs(),
      ])
      setClub(c)
      setName(c.name)
      setLogoUrl(c.logo_url ?? '')
      setColor(c.primary_color ?? '')
      setSecondaryColor(c.secondary_color ?? '')
      setAccentColor(c.accent_color ?? '')
      setSeatLimit(c.seat_limit != null ? String(c.seat_limit) : '')
      setDivision(c.division ?? '')
      setServiceUntil(c.service_until ?? '')
      setStaff(s.staff)
      setStaffInvites(inv.invitations)
      setKits(k.kits)
      setAllClubs(allC.clubs)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }

  useEffect(() => {
    if (Number.isFinite(id)) void reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  async function saveBranding(e: React.FormEvent) {
    e.preventDefault()
    if (color && !HEX_RE.test(color)) {
      setError('El color debe tener el formato #RRGGBB.')
      return
    }
    if (secondaryColor && !HEX_RE.test(secondaryColor)) {
      setError('El color secundario debe tener el formato #RRGGBB.')
      return
    }
    if (accentColor && !HEX_RE.test(accentColor)) {
      setError('El color de acento debe tener el formato #RRGGBB.')
      return
    }
    let seatLimitValue: number | null = null
    if (seatLimit.trim() !== '') {
      const parsed = Number(seatLimit.trim())
      if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        setError('El cupo de licencias debe ser un entero >= 0 (vacío = sin límite).')
        return
      }
      seatLimitValue = parsed
    }
    setSaving(true)
    setError(null)
    try {
      await admin.updateClub(id, {
        name: name.trim(),
        logo_url: logoUrl.trim(),
        primary_color: color.trim(),
        secondary_color: secondaryColor.trim(),
        accent_color: accentColor.trim(),
        seat_limit: seatLimitValue,
        division: division.trim(),
        service_until: serviceUntil.trim() || null,
      })
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setSaving(false)
    }
  }

  async function addPlayer(e: React.FormEvent) {
    e.preventDefault()
    const resolved = resolveUserTarget(playerQuery, playerPick)
    if ('error' in resolved) {
      setError(resolved.error)
      return
    }
    setAddingPlayer(true)
    setError(null)
    setPlayerNotice(null)
    try {
      const res = await admin.assignPlayers(
        id,
        'user_id' in resolved.target
          ? { user_ids: [resolved.target.user_id] }
          : { emails: [resolved.target.email] },
      )
      setPlayerQuery('')
      setPlayerPick(null)
      setPlayerNotice(
        res.updated === 0
          ? `${resolved.label} ya estaba en este club.`
          : res.pin_mails > 0
            ? `${resolved.label} quedó en el plantel. Le mandamos el mail para elegir su PIN, con el código de club.`
            : `${resolved.label} quedó en el plantel.`,
      )
      await reload()
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        // El email no tiene cuenta: el camino no es buscar otro id, es crear
        // al jugador — con su club ya puesto y su mail de PIN encolado.
        setCreatePlayerEmail('email' in resolved.target ? resolved.target.email : null)
        setError(e.message)
      } else {
        setError(
          e instanceof ApiError
            ? e.message.replace(/^seat_limit_reached:\s*/, '')
            : 'Error de red.',
        )
      }
    } finally {
      setAddingPlayer(false)
    }
  }

  async function addStaff(e: React.FormEvent) {
    e.preventDefault()
    const resolved = resolveUserTarget(staffQuery, staffPick)
    if ('error' in resolved) {
      setStaffNotFound(null)
      setError(resolved.error)
      return
    }
    setAddingStaff(true)
    setError(null)
    setStaffNotFound(null)
    try {
      await admin.addClubStaff(id, resolved.target, 'manager')
      setStaffQuery('')
      setStaffPick(null)
      await reload()
    } catch (e) {
      // 404 acá es siempre lo mismo: esa cuenta no existe todavía. No es un
      // error del form, es la señal de que el camino es la invitación.
      if (e instanceof ApiError && e.status === 404) {
        setStaffNotFound(e.message)
      } else {
        setError(e instanceof ApiError ? e.message : 'Error de red.')
      }
    } finally {
      setAddingStaff(false)
    }
  }

  async function removeStaff(staffId: number) {
    if (!window.confirm('¿Quitar este staff del club?')) return
    setError(null)
    try {
      await admin.removeClubStaff(id, staffId)
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    }
  }

  async function createKit(e: React.FormEvent) {
    e.preventDefault()
    setCreatingKit(true)
    setError(null)
    try {
      await admin.createKit(id, kitLabel.trim() || undefined, undefined, kitCode.trim() || undefined)
      setKitLabel('')
      setKitCode('')
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setCreatingKit(false)
    }
  }

  async function toggleKitActive(kit: AdminKit) {
    setKitBusyId(kit.id)
    setError(null)
    try {
      await admin.updateKit(kit.id, { active: !kit.active })
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setKitBusyId(null)
    }
  }

  async function regenerateKit(kit: AdminKit) {
    if (
      !window.confirm(
        `¿Regenerar el código de club "${kit.label || kit.access_code}"? El código viejo deja de servir.`,
      )
    )
      return
    setKitBusyId(kit.id)
    setError(null)
    try {
      await admin.regenerateKitCode(kit.id)
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setKitBusyId(null)
    }
  }

  async function fixKitCode(kit: AdminKit) {
    const next = window.prompt(
      `Nuevo código para "${kit.label || kit.access_code}" (3 a 8 letras o números, ej. KIT1). El código actual deja de servir.`,
      kit.access_code,
    )
    if (next === null || !next.trim() || next.trim().toUpperCase() === kit.access_code) return
    setKitBusyId(kit.id)
    setError(null)
    try {
      await admin.updateKit(kit.id, { access_code: next.trim() })
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setKitBusyId(null)
    }
  }

  async function moveKit(kit: AdminKit) {
    const targetRaw = moveTargetByKit[kit.id]
    const targetClubId = Number(targetRaw)
    if (!targetRaw || !Number.isFinite(targetClubId) || targetClubId <= 0) {
      setError('Elegí un club destino para mover el código.')
      return
    }
    if (targetClubId === kit.club_id) {
      setError('El código ya pertenece a ese club.')
      return
    }
    const targetName = allClubs?.find((c) => c.id === targetClubId)?.name ?? `club #${targetClubId}`
    if (
      !window.confirm(
        `¿Mover el código ${kit.access_code}${kit.label ? ` ("${kit.label}")` : ''} a "${targetName}"? Deja de aparecer en este club; el código no cambia.`,
      )
    )
      return
    setKitBusyId(kit.id)
    setError(null)
    try {
      await admin.updateKit(kit.id, { club_id: targetClubId })
      setMoveTargetByKit((prev) => {
        const next = { ...prev }
        delete next[kit.id]
        return next
      })
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setKitBusyId(null)
    }
  }

  async function copyKitCode(kit: AdminKit) {
    await navigator.clipboard.writeText(kit.access_code)
    setCopiedKitId(kit.id)
    window.setTimeout(() => setCopiedKitId(null), 1500)
  }

  async function createInvite(e: React.FormEvent) {
    e.preventDefault()
    setCreatingInvite(true)
    setError(null)
    try {
      const res = await admin.createStaffInvite(id, {
        club_role: 'manager' as const,
        email: inviteEmail.trim() || undefined,
      })
      setLastInvite(res)
      setInviteLinkCopied(false)
      setInviteEmail('')
      await reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error de red.')
    } finally {
      setCreatingInvite(false)
    }
  }

  if (!club) {
    return (
      <section>
        {error ? <div className="banner-err">{error}</div> : <p className="text-sm text-text-muted">Cargando…</p>}
      </section>
    )
  }

  return (
    <section>
      <div className="page-header">
        <div className="flex items-center gap-md">
          <ClubMark
            logoUrl={club.logo_url}
            name={club.name}
            size={44}
            radius={6}
            fallbackColor={club.primary_color}
          />
          <div className="flex flex-col leading-tight">
            <h1>{club.name}</h1>
            <span className="text-sm text-text-muted">
              {club.division || 'sin división'} · club #{club.id} · alta{' '}
              {new Date(club.created_at).toLocaleDateString('es-AR')}
            </span>
          </div>
        </div>
        <Link to="/admin/clubs" className="btn no-underline">
          ← Clubes
        </Link>
      </div>

      {!club.service_active && (
        <div className="banner-err mb-md">
          Servicio vencido
          {club.service_until
            ? ` desde el ${new Date(`${club.service_until}T00:00:00`).toLocaleDateString('es-AR')}`
            : ''}
          . Los jugadores de este club no pueden entrar al VR hasta renovar
          `service_until`.
        </div>
      )}

      {error && <div className="banner-err mb-md">{error}</div>}

      <ClubSummary club={club} />

      <h2>Marca</h2>
      <form onSubmit={saveBranding} className="card flex flex-col gap-lg">
        <div className="grid gap-md md:grid-cols-2">
          <label className="flex flex-col gap-xs text-sm">
            Nombre
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-xs text-sm">
            División / categoría del plantel
            <input
              type="text"
              className="input"
              placeholder="Ej: Primera, Sub-15"
              maxLength={64}
              value={division}
              onChange={(e) => setDivision(e.target.value)}
            />
            <span className="text-xs text-text-muted">
              Texto libre; vacío = sin división. Se muestra en el header de `/club`.
            </span>
          </label>
        </div>

        <ClubLogoField
          value={logoUrl}
          onChange={setLogoUrl}
          name={name}
          fallbackColor={HEX_RE.test(color.trim()) ? color.trim() : null}
        />

        {/* Los tres colores van en una fila: son el mismo tipo de dato y se
            eligen mirándolos juntos, no uno abajo del otro con media pantalla
            vacía al costado (mg, 2026-09-17). */}
        <div className="grid gap-md sm:grid-cols-2 lg:grid-cols-3">
          <ColorField
            label="Color de marca"
            value={color}
            onChange={setColor}
            emptyHint="Se usa el de VerSports."
          />
          <ColorField
            label="Color secundario"
            value={secondaryColor}
            onChange={setSecondaryColor}
            emptyHint="Se cae al de marca."
          />
          <ColorField
            label="Color de acento"
            value={accentColor}
            onChange={setAccentColor}
            emptyHint="Se cae al de marca."
          />
        </div>

        <div className="grid gap-md sm:grid-cols-2">
          <label className="flex flex-col gap-xs text-sm">
            Cupo de licencias (seat_limit)
            <input
              type="number"
              min={0}
              step={1}
              className="input"
              placeholder="Sin límite"
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
            />
            <span className="text-xs text-text-muted">
              Vacío = sin límite. Cuenta jugadores del club (no staff ni invitaciones).
            </span>
          </label>
          <label className="flex flex-col gap-xs text-sm">
            Servicio habilitado hasta
            <input
              type="date"
              className="input"
              value={serviceUntil}
              onChange={(e) => setServiceUntil(e.target.value)}
            />
            <span className="text-xs text-text-muted">
              Vacío = sin vencimiento. Pasada la fecha los jugadores pierden el VR;
              el manager sigue entrando a `/club`.
            </span>
          </label>
        </div>

        <div>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </form>

      <h2>
        Jugadores ({club.player_count} / {club.seat_limit ?? '∞'})
      </h2>
      <p className="text-xs text-text-muted mb-sm">
        Sumá al plantel una cuenta que YA existe: buscala por email (o nombre)
        y elegila de la lista, o pegá el email completo. Si todavía no tiene
        cuenta, se crea acá mismo y le llega el mail para elegir su PIN.
      </p>
      <form onSubmit={addPlayer} className="filter-bar items-start">
        <UserPicker
          query={playerQuery}
          onQueryChange={(q) => {
            setPlayerQuery(q)
            setPlayerNotice(null)
          }}
          picked={playerPick}
          onPick={setPlayerPick}
          search={async (q) => (await admin.users(q)).users}
          disabled={addingPlayer}
          actionLabel="Vas a sumar al plantel"
          notFoundHint={
            <>
              Si todavía no tiene cuenta,{' '}
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => setCreatePlayerEmail(playerQuery.trim() || null)}
              >
                creala como jugador de este club
              </button>
              .
            </>
          }
        />
        <button type="submit" className="btn" disabled={addingPlayer}>
          {addingPlayer ? 'Agregando…' : '+ Agregar jugador'}
        </button>
      </form>
      {playerNotice && <div className="banner-ok mb-md">{playerNotice}</div>}

      <div className="table-wrap mb-xl">
        <table>
          <thead>
            <tr>
              <th>user_id</th>
              <th>Nombre</th>
              <th>Email</th>
              <th>Alta</th>
              <th>Último login</th>
            </tr>
          </thead>
          <tbody>
            {club.players.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted">
                  Sin jugadores todavía.
                </td>
              </tr>
            )}
            {club.players.map((p) => (
              <tr key={p.user_id}>
                <td>{p.user_id}</td>
                <td>{p.display_name}</td>
                <td>{p.email ?? '—'}</td>
                <td className="text-sm text-text-secondary">
                  {p.date_joined ? new Date(p.date_joined).toLocaleDateString('es-AR') : '—'}
                </td>
                <td className="text-sm text-text-secondary">
                  {p.last_login ? new Date(p.last_login).toLocaleDateString('es-AR') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Códigos de club ({activeKits.length})</h2>
      <p className="text-xs text-text-muted mb-sm">
        Con el código de club se ligan la tablet y los visores al club; después
        cada jugador entra con su PIN. Un club puede tener varios códigos (ej.
        uno por sede o por cancha).
      </p>
      <form onSubmit={createKit} className="filter-bar">
        <input
          type="text"
          placeholder="label (opcional)"
          value={kitLabel}
          onChange={(e) => setKitLabel(e.target.value)}
          className="input max-w-[220px]"
        />
        <input
          type="text"
          placeholder="código (opcional, ej. KIT1)"
          value={kitCode}
          onChange={(e) => setKitCode(e.target.value.toUpperCase())}
          maxLength={8}
          className="input max-w-[220px] font-mono"
        />
        <button type="submit" className="btn" disabled={creatingKit}>
          {creatingKit ? 'Creando…' : '+ Crear código'}
        </button>
      </form>
      {inactiveKitCount > 0 && (
        <button
          type="button"
          className="btn-ghost mb-sm px-sm py-xs text-xs"
          onClick={() => setShowInactiveKits((v) => !v)}
        >
          {showInactiveKits ? 'Ocultar inactivos' : `Mostrar inactivos (${inactiveKitCount})`}
        </button>
      )}

      <div className="table-wrap mb-xl">
        <table>
          <thead>
            <tr>
              <th>Label</th>
              <th>Código de club</th>
              <th>Activo</th>
              <th>Creado</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibleKits.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted">
                  Sin códigos activos.
                </td>
              </tr>
            )}
            {visibleKits.map((k) => (
              <tr key={k.id}>
                <td>{k.label || <span className="text-text-muted">—</span>}</td>
                <td>
                  <div className="flex items-center gap-sm">
                    <code className="font-mono text-sm">{k.access_code}</code>
                    <button
                      type="button"
                      className="btn-ghost px-sm py-xs text-xs"
                      onClick={() => void copyKitCode(k)}
                    >
                      {copiedKitId === k.id ? 'Copiado ✓' : 'Copiar'}
                    </button>
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-ghost px-sm py-xs text-xs"
                    disabled={kitBusyId === k.id}
                    onClick={() => void toggleKitActive(k)}
                  >
                    <span className={k.active ? 'tag-ok' : 'tag'}>
                      {k.active ? 'activo' : 'inactivo'}
                    </span>
                  </button>
                </td>
                <td className="text-sm text-text-secondary">
                  {new Date(k.created_at).toLocaleDateString('es-AR')}
                </td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-sm">
                    <select
                      className="input max-w-[180px] py-xs text-xs"
                      value={moveTargetByKit[k.id] ?? ''}
                      disabled={kitBusyId === k.id}
                      onChange={(e) =>
                        setMoveTargetByKit((prev) => ({ ...prev, [k.id]: e.target.value }))
                      }
                    >
                      <option value="">Mover a…</option>
                      {allClubs
                        ?.filter((c) => c.id !== k.club_id)
                        .map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="btn-ghost px-sm py-xs text-xs"
                      disabled={kitBusyId === k.id || !moveTargetByKit[k.id]}
                      onClick={() => void moveKit(k)}
                    >
                      Mover
                    </button>
                    <button
                      type="button"
                      className="btn-ghost px-sm py-xs text-xs"
                      disabled={kitBusyId === k.id}
                      onClick={() => void fixKitCode(k)}
                    >
                      Fijar código
                    </button>
                    <button
                      type="button"
                      className="btn-ghost px-sm py-xs text-xs text-danger"
                      disabled={kitBusyId === k.id}
                      onClick={() => void regenerateKit(k)}
                    >
                      Regenerar código
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Staff ({staff?.length ?? 0})</h2>
      <p className="text-xs text-text-muted mb-sm">
        Suma como manager del club a una cuenta que YA existe: buscala por
        email (o nombre) y elegila de la lista, o pegá el email completo. Si no
        tiene cuenta todavía, el camino es la invitación de acá abajo.
      </p>
      <form onSubmit={addStaff} className="filter-bar items-start">
        <UserPicker
          query={staffQuery}
          onQueryChange={(q) => {
            setStaffQuery(q)
            setStaffNotFound(null)
          }}
          picked={staffPick}
          onPick={setStaffPick}
          search={async (q) => (await admin.users(q)).users}
          disabled={addingStaff}
        />
        <button type="submit" className="btn" disabled={addingStaff}>
          {addingStaff ? 'Agregando…' : '+ Agregar staff'}
        </button>
      </form>
      {staffNotFound && (
        <div className="banner-warn mb-md">
          {staffNotFound} El link se genera en{' '}
          <strong>Invitaciones de staff</strong>, acá abajo.
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Email</th>
              <th>Alta</th>
              <th>Último login</th>
              <th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {staff?.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-text-muted">
                  Sin staff asignado.
                </td>
              </tr>
            )}
            {staff?.map((s) => (
              <tr key={s.id}>
                <td>{s.username}</td>
                <td>{s.email ?? '—'}</td>
                <td className="text-sm text-text-secondary">
                  {s.date_joined ? new Date(s.date_joined).toLocaleDateString('es-AR') : '—'}
                </td>
                <td className="text-sm text-text-secondary">
                  {s.last_login ? new Date(s.last_login).toLocaleDateString('es-AR') : '—'}
                </td>
                <td className="text-right">
                  <button
                    type="button"
                    className="btn-ghost text-xs text-danger"
                    onClick={() => void removeStaff(s.id)}
                  >
                    Quitar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2>Invitaciones de staff (self-signup)</h2>
      <p className="text-xs text-text-muted mb-sm">
        Generá un link de único uso para que alguien se registre directo como
        staff de este club (bootstrap del primer manager, sin necesitar un
        user_id existente). El link ES la credencial — cualquiera que lo abra
        puede usarlo, y el email no restringe con qué cuenta se registra (solo
        precarga el form). Con email cargado, además, el sistema ENCOLA el mail
        de invitación con el link: sale por la cola de{' '}
        <Link to="/admin/emails" className="underline">
          /admin/emails
        </Link>
        , sujeto al freno global de envío. Sin email no se manda nada.
      </p>
      <form onSubmit={createInvite} className="filter-bar">
        <input
          type="email"
          placeholder="email (opcional — le encola el mail)"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          className="input max-w-[280px]"
        />
        <button type="submit" className="btn" disabled={creatingInvite}>
          {inviteEmail.trim()
            ? creatingInvite
              ? 'Generando y encolando…'
              : '+ Generar link y encolar mail'
            : creatingInvite
              ? 'Generando…'
              : '+ Generar link (sin mail)'}
        </button>
      </form>

      {lastInvite && (
        <StaffInviteOutcome
          result={lastInvite}
          copied={inviteLinkCopied}
          onCopy={() => {
            void navigator.clipboard.writeText(lastInvite.link_url)
            setInviteLinkCopied(true)
          }}
        />
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th title="Con email, al generarla se encoló el mail de invitación; si salió o no se ve en /admin/emails.">
                Email
              </th>
              <th>Estado</th>
              <th>Vence</th>
              <th className="text-right">Link</th>
            </tr>
          </thead>
          <tbody>
            {staffInvites?.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center text-text-muted">
                  Sin invitaciones generadas.
                </td>
              </tr>
            )}
            {staffInvites?.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.email ?? '—'}</td>
                <td>
                  <span className={inv.status === 'pending' ? 'tag-ok' : 'tag'}>
                    {INVITE_STATUS_LABEL[inv.status]}
                  </span>
                </td>
                <td className="text-sm text-text-secondary">
                  {new Date(inv.expires_at).toLocaleDateString('es-AR', { dateStyle: 'short' })}
                </td>
                <td className="text-right">
                  {inv.status === 'pending' && (
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      onClick={() =>
                        void navigator.clipboard.writeText(`${window.location.origin}/staff-invite/${inv.token}`)
                      }
                    >
                      Copiar link
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createPlayerEmail !== null && (
        <CreateUserModal
          initialEmail={createPlayerEmail}
          initialClubId={id}
          lockClub
          onClose={() => setCreatePlayerEmail(null)}
          onUserCreated={(u) => {
            setCreatePlayerEmail(null)
            setPlayerQuery('')
            setPlayerPick(null)
            setError(null)
            setPlayerNotice(`${u.email ?? u.username} quedó creado en el plantel.`)
            void reload()
          }}
        />
      )}
    </section>
  )
}

/**
 * Resumen de estado del club, arriba de todo y antes de cualquier sección
 * editable: qué contrató (licencias, servicio), quién lo opera (staff, kits)
 * y si lo está usando (actividad). Mismos umbrales y mismas palabras que la
 * fila de `/admin/clubs` — las dos vistas comparten `clubOps.ts`, así que un
 * club "por vencer" en la lista no aparece "en orden" acá.
 *
 * Las cifras salen de la propia respuesta del detalle (el backend manda las
 * mismas métricas que en la lista), no de un segundo fetch.
 */
function ClubSummary({ club }: { club: AdminClubDetailT }) {
  const seats = seatUsage(club)
  const service = serviceStatus(club)
  const alerts = clubAlerts(club)

  return (
    <div className="mb-lg flex flex-col gap-md">
      <div className="grid grid-cols-2 gap-md sm:grid-cols-3 lg:grid-cols-5">
        <SummaryCard
          label="Licencias"
          value={seats.label}
          tone={seats.tone}
          hint={seats.detail}
        />
        <SummaryCard
          label="Servicio"
          value={service.label}
          tone={service.tone}
          hint={service.detail}
        />
        <SummaryCard
          label="Staff"
          value={String(club.staff_count)}
          tone={club.staff_count === 0 ? 'muted' : club.staff_pending_terms > 0 ? 'warn' : 'ok'}
          hint={
            club.staff_count === 0
              ? `Sin staff. ${plural(club.staff_invites_pending, 'invitación', 'invitaciones')} sin usar.`
              : `${club.staff_pending_terms} sin aceptar los T&C · ${plural(club.staff_invites_pending, 'invitación pendiente', 'invitaciones pendientes')}.`
          }
        />
        <SummaryCard
          label="Códigos de club"
          value={String(club.kit_count)}
          tone={club.kit_count === 0 ? 'muted' : club.kits_active > 0 ? 'ok' : 'warn'}
          hint={`${club.kits_active} activos.`}
        />
        <SummaryCard
          label="Última sesión"
          value={relativeDay(club.last_session_at)}
          tone={club.sessions_7d > 0 ? 'ok' : 'muted'}
          hint={`${plural(club.sessions_7d, 'sesión', 'sesiones')} en los últimos 7 días.`}
        />
      </div>

      {alerts.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-xs p-0 text-sm text-text-secondary">
          {alerts.map((a) => (
            <li key={a}>· {a}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SummaryCard({
  label,
  value,
  tone,
  hint,
}: {
  label: string
  value: string
  tone: Parameters<typeof tagClass>[0]
  hint: string
}) {
  return (
    <div className="stat-card" title={hint}>
      <div className="stat-card-label">{label}</div>
      <div className="mt-xs">
        <span className={tagClass(tone)}>{value}</span>
      </div>
      <div className="mt-xs text-xs leading-snug text-text-muted">{hint}</div>
    </div>
  )
}
